import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import {
  fetchExpenseTypes,
  createExpenseType,
  updateExpenseType,
  updateExpenseTypeStatus,
  deleteExpenseType,
} from '../services/expenseService';

const initialForm = { name: '', description: '' };

export default function ExpenseTypes() {
  const [types, setTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search]);

  const loadTypes = async () => {
    try {
      setLoading(true);
      const res = await fetchExpenseTypes({ page, limit, search });
      const records = res.data?.data?.records || [];
      const meta = res.data?.data?.meta || {};
      setTypes(records);
      setTotalPages(meta.pages || 1);
      setTotalRecords(meta.total || records.length);
    } catch (error) {
      toast.error('Failed to load expense types');
    } finally {
      setLoading(false);
    }
  };

  const openDrawer = (type = null) => {
    if (type) {
      setEditingId(type.id);
      setForm({ name: type.name || '', description: type.description || '' });
    } else {
      setEditingId(null);
      setForm(initialForm);
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setForm(initialForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      if (editingId) {
        await updateExpenseType(editingId, { ...form, name: form.name.trim() });
        toast.success('Expense type updated');
      } else {
        await createExpenseType({ ...form, name: form.name.trim() });
        toast.success('Expense type created');
      }
      closeDrawer();
      loadTypes();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save expense type');
    }
  };

  const handleDelete = async (type) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete expense type?',
      text: `Are you sure you want to delete ${type.name}?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await deleteExpenseType(type.id);
      toast.success('Expense type deleted');
      loadTypes();
    } catch (error) {
      toast.error('Failed to delete expense type');
    }
  };

  const handleToggleStatus = async (type) => {
    const result = await Swal.fire({
      icon: 'question',
      title: `${type.isActive ? 'Deactivate' : 'Activate'} expense type?`,
      text: `Are you sure you want to ${type.isActive ? 'deactivate' : 'activate'} ${type.name}?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, continue',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await updateExpenseTypeStatus(type.id, !type.isActive);
      toast.success('Expense type status updated');
      loadTypes();
    } catch (error) {
      toast.error('Failed to update expense type status');
    }
  };

  const totalStart = (page - 1) * limit + 1;
  const totalEnd = Math.min(totalStart + limit - 1, totalRecords);

  return (
    <LayoutWithSidebar>
      <div className="p-6 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Expense Types</h1>
            <p className="text-sm text-gray-500">Define categories for tracking shop expenses.</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder="Search by name"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => openDrawer()}
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-md text-sm shadow hover:bg-indigo-700"
            >
              <Plus size={16} />
              Add Expense Type
            </button>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Description</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                      Loading expense types...
                    </td>
                  </tr>
                ) : types.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                      No expense types found.
                    </td>
                  </tr>
                ) : (
                  types.map((type) => (
                    <tr key={type.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{type.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{type.description || '-'}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            type.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {type.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleToggleStatus(type)}
                            className="p-2 text-indigo-600 hover:text-indigo-800 rounded-full hover:bg-indigo-50"
                          >
                            {type.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                          </button>
                          <button
                            type="button"
                            onClick={() => openDrawer(type)}
                            className="p-2 text-indigo-600 hover:text-indigo-800 rounded-full hover:bg-indigo-50"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(type)}
                            className="p-2 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-600">
            <div>
              Showing {totalRecords === 0 ? 0 : totalStart}-{totalEnd} of {totalRecords}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
                className={`px-3 py-1 border rounded-md ${page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 border-gray-300 hover:bg-gray-100'}`}
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page >= totalPages}
                className={`px-3 py-1 border rounded-md ${page >= totalPages ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 border-gray-300 hover:bg-gray-100'}`}
              >
                Next
              </button>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="border border-gray-300 rounded-md px-2 py-1"
              >
                {[10, 20, 50].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end">
          <div className="w-full max-w-md bg-white h-full shadow-xl flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {editingId ? 'Edit Expense Type' : 'Create Expense Type'}
                </h2>
                <p className="text-xs text-gray-500">
                  Provide a name and optional description for the expense category.
                </p>
              </div>
              <button type="button" onClick={closeDrawer} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  rows={4}
                />
              </div>
            </form>
            <div className="border-t px-6 py-4 flex justify-end gap-3 bg-gray-50">
              <button
                type="button"
                onClick={closeDrawer}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md shadow hover:bg-indigo-700"
              >
                {editingId ? 'Update Type' : 'Create Type'}
              </button>
            </div>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}
